export interface PasswordStrength {
  score: number // 0-4
  label: string
  color: string
  gradient: string
  requirements: {
    length: boolean
    uppercase: boolean
    lowercase: boolean
    number: boolean
    special: boolean
  }
}

export const calculatePasswordStrength = (password: string): PasswordStrength => {
  const requirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  }

  const metRequirements = Object.values(requirements).filter(Boolean).length
  let score = 0
  let label = ''
  let color = ''
  let gradient = ''

  if (password.length === 0) {
    score = 0
    label = ''
    color = 'transparent'
    gradient = 'from-transparent to-transparent'
  } else if (metRequirements <= 1) {
    score = 1
    label = 'Very Weak'
    color = 'rgb(239, 68, 68)' // red-500
    gradient = 'from-red-500 to-red-600'
  } else if (metRequirements === 2) {
    score = 2
    label = 'Weak'
    color = 'rgb(245, 158, 11)' // amber-500
    gradient = 'from-amber-500 to-orange-500'
  } else if (metRequirements === 3) {
    score = 3
    label = 'Good'
    color = 'rgb(59, 130, 246)' // blue-500
    gradient = 'from-blue-500 to-purple-500'
  } else if (metRequirements === 4) {
    score = 4
    label = 'Strong'
    color = 'rgb(34, 197, 94)' // emerald-500
    gradient = 'from-emerald-500 to-teal-500'
  } else if (metRequirements === 5) {
    score = 5
    label = 'Very Strong'
    color = 'rgb(168, 85, 247)' // purple-500
    gradient = 'from-purple-500 to-pink-500'
  }

  return {
    score,
    label,
    color,
    gradient,
    requirements
  }
}

export const getPasswordRequirements = () => [
  { key: 'length', label: 'At least 8 characters' },
  { key: 'uppercase', label: 'One uppercase letter' },
  { key: 'lowercase', label: 'One lowercase letter' },
  { key: 'number', label: 'One number' },
  { key: 'special', label: 'One special character (!@#$%^&*)' }
]

export const isPasswordValid = (password: string): boolean => {
  const strength = calculatePasswordStrength(password)
  return Object.values(strength.requirements).every(Boolean)
} 